import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../services/api';

interface Deposit {
  id: string;
  clientName: string;
  clientEmail?: string;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'deleted';
  attachmentsCount: number;
}

interface DepositDetail {
  id: string;
  content: string;
  clientName: string;
  clientEmail?: string;
  clientCompany?: string;
  createdAt: string;
  expiresAt: string;
  attachments: Array<{
    id: string;
    filename: string;
    mimeType: string;
    fileSize: number;
  }>;
}

export default function DepositsPage() {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedDeposit, setSelectedDeposit] = useState<DepositDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const loadDeposits = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.listDeposits({
        page,
        limit: 20,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setDeposits(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Failed to load deposits:', error);
      toast.error('Erreur lors du chargement des dépôts');
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    loadDeposits();
  }, [loadDeposits]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadDeposits();
  };

  const handleViewDeposit = async (id: string) => {
    setIsLoadingDetail(true);
    try {
      const response = await adminApi.getDeposit(id);
      setSelectedDeposit(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du dépôt');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleDeleteDeposit = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce dépôt ?')) return;

    try {
      await adminApi.deleteDeposit(id);
      toast.success('Dépôt supprimé');
      setSelectedDeposit(null);
      loadDeposits();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownload = async (depositId: string, attachmentId: string, filename: string) => {
    try {
      const response = await adminApi.downloadDepositAttachment(depositId, attachmentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement démarré');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">Actif</span>;
      case 'expired':
        return <span className="badge badge-warning">Expiré</span>;
      case 'deleted':
        return <span className="badge badge-error">Supprimé</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dépôts clients</h1>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="card mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom de client..."
              className="input"
            />
          </div>
          <select
            className="input w-auto"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="expired">Expiré</option>
          </select>
          <button type="submit" className="btn-primary">
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Rechercher
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner spinner-lg" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de dépôt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expire le
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fichiers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deposits.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-gray-500">Aucun dépôt client pour le moment</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Les dépôts apparaîtront ici lorsque les clients soumettront des informations
                      </p>
                    </td>
                  </tr>
                ) : (
                  deposits.map((deposit) => (
                    <tr key={deposit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{deposit.clientName}</div>
                        {deposit.clientEmail && (
                          <div className="text-sm text-gray-500">{deposit.clientEmail}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(deposit.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(deposit.expiresAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {deposit.attachmentsCount > 0 ? (
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            {deposit.attachmentsCount}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(deposit.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => handleViewDeposit(deposit.id)}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                        >
                          Voir
                        </button>
                        <button
                          onClick={() => handleDeleteDeposit(deposit.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-outline"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-500">
              Page {page} sur {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-outline"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedDeposit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Détail du dépôt</h2>
                <button
                  onClick={() => setSelectedDeposit(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {isLoadingDetail ? (
                <div className="flex items-center justify-center h-48">
                  <div className="spinner spinner-lg" />
                </div>
              ) : (
                <>
                  {/* Client info */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Client</h3>
                    <p className="text-gray-900 font-medium">{selectedDeposit.clientName}</p>
                    {selectedDeposit.clientEmail && (
                      <p className="text-gray-600">{selectedDeposit.clientEmail}</p>
                    )}
                    {selectedDeposit.clientCompany && (
                      <p className="text-gray-500">{selectedDeposit.clientCompany}</p>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Date de dépôt</h3>
                      <p className="text-gray-900">{formatDate(selectedDeposit.createdAt)}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Expire le</h3>
                      <p className="text-gray-900">{formatDate(selectedDeposit.expiresAt)}</p>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Message</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-900 whitespace-pre-wrap">{selectedDeposit.content}</p>
                    </div>
                  </div>

                  {/* Attachments */}
                  {selectedDeposit.attachments.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">
                        Pièces jointes ({selectedDeposit.attachments.length})
                      </h3>
                      <div className="space-y-2">
                        {selectedDeposit.attachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(attachment.fileSize)}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDownload(selectedDeposit.id, attachment.id, attachment.filename)}
                              className="btn-outline text-sm py-1 px-3"
                            >
                              Télécharger
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setSelectedDeposit(null)}
                      className="btn-outline"
                    >
                      Fermer
                    </button>
                    <button
                      onClick={() => handleDeleteDeposit(selectedDeposit.id)}
                      className="btn-primary bg-red-600 hover:bg-red-700"
                    >
                      Supprimer le dépôt
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
